/*
 * @Description: 定位设置弹窗（对齐设计图 + ai_device_web 采集设置交互）。
 *   采集时段（上班/下班时间分选）/ 工作日（预设 + 自定义）/ 采集定位模式 /
 *   允许成员查看个人轨迹 走 G1-2 config/settings；
 *   异常提醒设置（连续移动超距 / 停留时长超限 / 设备下线）走 G3-4 config/alert-rules。
 *   确定时两套接口一并保存。
 */
import React, { useEffect, useState } from 'react'
import {
  Modal,
  Select,
  Switch,
  InputNumber,
  TimePicker,
  Checkbox,
  Button,
  message
} from 'antd'
import { DownOutlined, UpOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import {
  buildWorkDays,
  CONTINUOUS_DISTANCE_RANGE,
  DEFAULT_ALERT_RULES,
  DEFAULT_LOCATION_SETTINGS,
  getWorkdayPreset,
  STAY_TIMEOUT_RANGE,
  LOCATION_MODE_OPTIONS,
  normalizeWorkDays,
  PRESET_WORK_DAYS,
  WEEK_OPTIONS,
  WORK_TIME_MINUTE_STEP,
  WORKDAY_PRESETS,
  WorkdayPreset
} from '../../constants'
import { useAlertRules } from '../../hooks/useAlertRules'
import { useLocationSettings } from '../../hooks/useLocationSettings'
import {
  AlertRulesDraft,
  LocationSettings as Settings
} from '../../types/settings.types'
import './index.scss'

const { Option } = Select
const TIME_FMT = 'HH:mm'

interface LocationSettingsProps {
  /** 是否显示 */
  visible: boolean
  /** 当前团队长 id */
  groupId: string
  /** 关闭 */
  onClose: () => void
}

/** 'HH:mm' → 分钟数，用于上班/下班先后校验 */
const toMinutes = (text: string): number => {
  const [h, m] = String(text || '0:0').split(':')
  return Number(h) * 60 + Number(m)
}

const LocationSettings: React.FC<LocationSettingsProps> = ({
  visible,
  groupId,
  onClose
}) => {
  const {
    settings,
    loading: loadingSettings,
    saving: savingSettings,
    save: saveSettings
  } = useLocationSettings(groupId, visible)
  const {
    rules,
    loading: loadingRules,
    saving: savingRules,
    save: saveRules
  } = useAlertRules(groupId, visible)
  const loading = loadingSettings || loadingRules
  const saving = savingSettings || savingRules

  // 采集配置草稿（G1-2 六字段，确定时提交；取消丢弃）
  const [draft, setDraft] = useState<Settings>(DEFAULT_LOCATION_SETTINGS)
  // 工作日 UI 态：预设方案 + 自定义勾选天（独立于 workDays，避免自定义恰好等于预设时跳档）
  const [workdayPreset, setWorkdayPreset] = useState<WorkdayPreset>('mon-fri')
  const [customDays, setCustomDays] = useState<string[]>([
    '1',
    '2',
    '3',
    '4',
    '5'
  ])
  // 异常提醒草稿（来自 G3，确定时逐条 G4 保存）
  const [alerts, setAlerts] = useState<AlertRulesDraft>(DEFAULT_ALERT_RULES)
  // 采集时段展开态（默认折叠）
  const [collectOpen, setCollectOpen] = useState(false)
  // 两个异常提醒项的展开态
  const [moveOpen, setMoveOpen] = useState(false)
  const [stayOpen, setStayOpen] = useState(false)

  // 接口数据到达 / 重新打开时，同步采集配置草稿与工作日 UI 态
  useEffect(() => {
    if (!visible || !settings) return
    setDraft(settings)
    const days = normalizeWorkDays(settings.workDays)
    const valid = days.length ? days : PRESET_WORK_DAYS['mon-fri']
    setWorkdayPreset(getWorkdayPreset(valid))
    setCustomDays(valid)
  }, [visible, settings])

  // 告警规则（G3）到达时同步异常提醒草稿
  useEffect(() => {
    if (visible && rules) setAlerts(rules)
  }, [visible, rules])

  const patch = (part: Partial<Settings>) =>
    setDraft((prev) => ({ ...prev, ...part }))
  const patchAlerts = (part: Partial<AlertRulesDraft>) =>
    setAlerts((prev) => ({ ...prev, ...part }))

  // 上班/下班时间选择
  const handleTimeChange =
    (which: 'start' | 'end') => (m: dayjs.Dayjs | null) => {
      if (!m) return
      const t = m.format(TIME_FMT)
      patch(which === 'start' ? { workStartTime: t } : { workEndTime: t })
    }

  // 工作日预设切换
  const handlePresetChange = (preset: WorkdayPreset) => {
    setWorkdayPreset(preset)
    if (preset === 'custom') {
      patch({ workDays: buildWorkDays('custom', customDays) })
    } else {
      const next = PRESET_WORK_DAYS[preset]
      setCustomDays(next)
      patch({ workDays: buildWorkDays(preset, next) })
    }
  }

  // 自定义工作日勾选
  const handleCustomChange = (values: (string | number)[]) => {
    const days = values.map(String)
    setCustomDays(days)
    patch({ workDays: buildWorkDays('custom', days) })
  }

  const handleOk = () => {
    if (toMinutes(draft.workStartTime) >= toMinutes(draft.workEndTime)) {
      message.error('上班时间不能晚于下班时间')
      return
    }
    if (workdayPreset === 'custom' && customDays.length === 0) {
      message.error('请至少选择一个工作日')
      return
    }
    // 采集配置（G2）+ 告警规则（G4）一并保存，全部成功才算成功
    Promise.all([saveSettings(draft), saveRules(alerts)]).then(
      ([okSettings, okRules]) => {
        if (okSettings && okRules) {
          message.success('已保存')
          onClose()
        } else {
          message.error('保存失败，请重试')
        }
      }
    )
  }

  return (
    <Modal
      title="定位设置"
      open={visible}
      onCancel={onClose}
      width={460}
      centered
      mask={{ closable: false }}
      // 固定高度随屏幕自适应（min/max 夹紧），内部滚动——展开异常提醒不再撑大弹窗
      styles={{
        body: {
          height: 'calc(100vh - 240px)',
          minHeight: 320,
          maxHeight: 560,
          overflowY: 'auto'
        }
      }}
      footer={[
        <Button key="cancel" onClick={onClose}>
          取消
        </Button>,
        <Button key="ok" type="primary" loading={saving} onClick={handleOk}>
          确定
        </Button>
      ]}
    >
      <div className="loc-settings" data-loading={loading ? '1' : '0'}>
        {/* 采集时段（可点击展开：上班/下班/工作日/节假日自动暂停） */}
        <div
          className={`loc-settings__collect ${collectOpen ? 'is-open' : ''}`}
        >
          <div
            className="loc-settings__collect-head"
            onClick={() => setCollectOpen((o) => !o)}
          >
            <span className="loc-settings__collect-title">采集时段</span>
            <span className="loc-settings__collect-summary">
              {draft.workStartTime} – {draft.workEndTime}
              {collectOpen ? (
                <UpOutlined className="loc-settings__collect-arrow" />
              ) : (
                <DownOutlined className="loc-settings__collect-arrow" />
              )}
            </span>
          </div>
          {collectOpen && (
            <div className="loc-settings__collect-body">
              <div className="loc-settings__field-row">
                <span className="loc-settings__field-label">上班时间</span>
                <TimePicker
                  className="loc-settings__field-ctrl"
                  value={dayjs(draft.workStartTime, TIME_FMT)}
                  format={TIME_FMT}
                  minuteStep={WORK_TIME_MINUTE_STEP}
                  allowClear={false}
                  onChange={handleTimeChange('start')}
                />
              </div>
              <div className="loc-settings__field-row">
                <span className="loc-settings__field-label">下班时间</span>
                <TimePicker
                  className="loc-settings__field-ctrl"
                  value={dayjs(draft.workEndTime, TIME_FMT)}
                  format={TIME_FMT}
                  minuteStep={WORK_TIME_MINUTE_STEP}
                  allowClear={false}
                  onChange={handleTimeChange('end')}
                />
              </div>
              <div className="loc-settings__field-row">
                <span className="loc-settings__field-label">工作日</span>
                <Select
                  className="loc-settings__field-ctrl"
                  value={workdayPreset}
                  onChange={handlePresetChange}
                  dropdownMatchSelectWidth={false}
                >
                  {WORKDAY_PRESETS.map((o) => (
                    <Option key={o.value} value={o.value}>
                      {o.label}
                    </Option>
                  ))}
                </Select>
              </div>
              {workdayPreset === 'custom' && (
                <div className="loc-settings__weekdays">
                  <Checkbox.Group
                    options={WEEK_OPTIONS}
                    value={customDays}
                    onChange={handleCustomChange}
                  />
                </div>
              )}
              <div className="loc-settings__field-row loc-settings__field-row--switch">
                <span className="loc-settings__field-label">
                  节假日自动暂停
                </span>
                <Switch
                  checked={draft.pauseOnHoliday}
                  onChange={(v) => patch({ pauseOnHoliday: v })}
                />
              </div>
              <div className="loc-settings__collect-hint">
                设备仅在上班时间内上报位置数据，时段外自动休眠以节省电量
              </div>
            </div>
          )}
        </div>

        {/* 采集定位模式（独立卡片行） */}
        <div className="loc-settings__mode-card">
          <span className="loc-settings__mode-label">采集定位模式</span>
          <Select
            className="loc-settings__mode-select"
            value={draft.locationMode}
            onChange={(v: Settings['locationMode']) =>
              patch({ locationMode: v })
            }
            dropdownMatchSelectWidth={false}
          >
            {LOCATION_MODE_OPTIONS.map((o) => (
              <Option key={o.value} value={o.value}>
                {o.label}
              </Option>
            ))}
          </Select>
        </div>

        {/* 允许成员查看个人轨迹（卡片行） */}
        <div className="loc-settings__mode-card">
          <span className="loc-settings__mode-label">允许成员查看个人轨迹</span>
          <Switch
            checked={draft.allowMemberViewOwnTrack}
            onChange={(v) => patch({ allowMemberViewOwnTrack: v })}
          />
        </div>

        {/* 异常提醒设置（G3-4 config/alert-rules） */}
        <div className="loc-settings__section">异常提醒设置</div>

        {/* 连续移动超距 */}
        <div className={`loc-settings__expand ${moveOpen ? 'is-open' : ''}`}>
          <div
            className="loc-settings__expand-head"
            onClick={() => setMoveOpen((o) => !o)}
          >
            <span className="loc-settings__label">连续移动超距</span>
            <span className="loc-settings__expand-value">
              超过 {alerts.continuousDistanceMeters} 米
              {moveOpen ? <UpOutlined /> : <DownOutlined />}
            </span>
          </div>
          {moveOpen && (
            <div className="loc-settings__expand-body">
              <div className="loc-settings__field-row">
                <span className="loc-settings__field-label">开启通知</span>
                <Switch
                  checked={alerts.continuousDistanceEnabled}
                  onChange={(v) =>
                    patchAlerts({ continuousDistanceEnabled: v })
                  }
                />
              </div>
              <div className="loc-settings__field-row">
                <span className="loc-settings__field-label">触发距离</span>
                <span className="loc-settings__sub-input">
                  <InputNumber
                    min={CONTINUOUS_DISTANCE_RANGE.min}
                    max={CONTINUOUS_DISTANCE_RANGE.max}
                    precision={0}
                    value={alerts.continuousDistanceMeters}
                    onChange={(v) =>
                      patchAlerts({
                        continuousDistanceMeters:
                          typeof v === 'number'
                            ? v
                            : DEFAULT_ALERT_RULES.continuousDistanceMeters
                      })
                    }
                  />
                  <em>米</em>
                </span>
              </div>
            </div>
          )}
        </div>

        {/* 停留时长超限 */}
        <div className={`loc-settings__expand ${stayOpen ? 'is-open' : ''}`}>
          <div
            className="loc-settings__expand-head"
            onClick={() => setStayOpen((o) => !o)}
          >
            <span className="loc-settings__label">停留时长超限</span>
            <span className="loc-settings__expand-value">
              超过 {alerts.stayTimeoutMinutes} 分钟
              {stayOpen ? <UpOutlined /> : <DownOutlined />}
            </span>
          </div>
          {stayOpen && (
            <div className="loc-settings__expand-body">
              <div className="loc-settings__field-row">
                <span className="loc-settings__field-label">开启通知</span>
                <Switch
                  checked={alerts.stayTimeoutEnabled}
                  onChange={(v) => patchAlerts({ stayTimeoutEnabled: v })}
                />
              </div>
              <div className="loc-settings__field-row">
                <span className="loc-settings__field-label">触发时长</span>
                <span className="loc-settings__sub-input">
                  <InputNumber
                    min={STAY_TIMEOUT_RANGE.min}
                    max={STAY_TIMEOUT_RANGE.max}
                    precision={0}
                    value={alerts.stayTimeoutMinutes}
                    onChange={(v) =>
                      patchAlerts({
                        stayTimeoutMinutes:
                          typeof v === 'number'
                            ? v
                            : DEFAULT_ALERT_RULES.stayTimeoutMinutes
                      })
                    }
                  />
                  <em>分钟</em>
                </span>
              </div>
            </div>
          )}
        </div>

        {/* 设备下线通知（卡片行） */}
        <div className="loc-settings__mode-card">
          <div className="loc-settings__mode-label-group">
            <span className="loc-settings__mode-label">设备下线通知</span>
            <span className="loc-settings__mode-desc">设备离线时发送通知</span>
          </div>
          <Switch
            checked={alerts.offlineNotifyEnabled}
            onChange={(v) => patchAlerts({ offlineNotifyEnabled: v })}
          />
        </div>
      </div>
    </Modal>
  )
}

export default LocationSettings
